import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, X, ShieldCheck, CheckCircle, MapPin, Clock, Sparkle, CurrencyDollar, Eye, Trash } from '@phosphor-icons/react';
import { getCategoryById, formatPrice } from '@/data/wellness';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
  suspended: 'bg-stone-100 text-stone-600 border-stone-200',
};

const AdminWellnessTab = () => {
  const [pros, setPros] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [integrity, setIntegrity] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [commissionPct, setCommissionPct] = useState(10);
  const [savingCommission, setSavingCommission] = useState(false);
  const [selected, setSelected] = useState(null);
  const [subTab, setSubTab] = useState('professionals');
  const [credentials, setCredentials] = useState([]);
  const [consultations, setConsultations] = useState([]);

  const fetchAll = useCallback(async () => {
    try {
      const [prosRes, metricsRes, settingsRes, credsRes, consRes, integrityRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/wellness/professionals`, { params: statusFilter ? { status: statusFilter } : {}, withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/wellness/metrics`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/wellness/settings`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/wellness/credentials`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/wellness/consultation-requests`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/wellness/integrity`, { withCredentials: true }),
      ]);
      setPros(prosRes.data);
      setMetrics(metricsRes.data);
      setCommissionPct(settingsRes.data.commission_pct);
      setCredentials(credsRes.data);
      setConsultations(consRes.data);
      setIntegrity(integrityRes.data);
    } catch (err) {
      toast.error('Failed to load wellness data');
    }
  }, [statusFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleApprove = async (id) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/wellness/professionals/${id}/approve`, {}, { withCredentials: true });
      toast.success('Professional approved');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt('Reason for rejection (optional):') || '';
    try {
      await axios.post(`${BACKEND_URL}/api/admin/wellness/professionals/${id}/reject`, { reason }, { withCredentials: true });
      toast.success('Professional rejected');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    }
  };

  const handleCredentialVerify = async (id) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/wellness/professionals/${id}/credential-verify`, {}, { withCredentials: true });
      toast.success('Credential marked as verified');
      fetchAll();
    } catch (err) {
      toast.error('Failed to verify credential');
    }
  };

  const handleDeletePro = async (id, name) => {
    if (!window.confirm(`Permanently delete "${name}"? This also removes their credentials.`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/wellness/professionals/${id}`, { withCredentials: true });
      toast.success('Professional deleted');
      fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  const handleVerifySingleCred = async (credId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/wellness/credentials/${credId}/verify`, {}, { withCredentials: true });
      toast.success('Credential verified');
      fetchAll();
    } catch (err) {
      toast.error('Failed to verify');
    }
  };

  const handleUnverifyCred = async (credId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/wellness/credentials/${credId}/unverify`, {}, { withCredentials: true });
      toast.success('Credential unverified');
      fetchAll();
    } catch (err) {
      toast.error('Failed');
    }
  };

  const handleSaveCommission = async () => {
    setSavingCommission(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/wellness/settings/commission`, { commission_pct: parseFloat(commissionPct) }, { withCredentials: true });
      toast.success('Commission updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update commission');
    } finally {
      setSavingCommission(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-wellness-tab">
      {/* Marketplace Integrity — real-time safety snapshot */}
      {integrity && (
        <div className="bg-gradient-to-br from-[#0F2A20] via-[#1A4331] to-[#2A5A44] text-white rounded-3xl p-6 md:p-7 relative overflow-hidden" data-testid="integrity-tile">
          <div className="pointer-events-none absolute -top-16 -right-16 w-52 h-52 bg-[#34D399]/20 rounded-full blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck size={20} weight="duotone" className="text-[#34D399]" />
              <p className="text-xs uppercase tracking-[0.16em] text-[#34D399] font-bold">Marketplace Integrity</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Bookable Pros', value: integrity.active_bookable_professionals, hi: true },
                { label: 'Credential Verified', value: integrity.credential_verified_professionals },
                { label: 'Approved (total)', value: integrity.approved_professionals },
                { label: 'Pending Credentials', value: integrity.pending_credentials, alert: integrity.pending_credentials > 0, onClick: () => setSubTab('credentials') },
                { label: 'Pending Pros', value: integrity.pending_professionals, onClick: () => setStatusFilter('pending') },
                { label: 'Suspended', value: integrity.suspended_professionals, warn: integrity.suspended_professionals > 0 },
                { label: 'Test-flagged', value: integrity.test_flagged_records, warn: integrity.test_flagged_records > 0 },
                { label: 'Open Reports', value: integrity.unresolved_reports, alert: integrity.unresolved_reports > 0 },
              ].map((s) => (
                <button
                  key={s.label}
                  onClick={s.onClick}
                  disabled={!s.onClick}
                  className={`text-left bg-white/8 backdrop-blur border rounded-2xl p-3 transition-all ${s.onClick ? 'hover:bg-white/15 cursor-pointer' : 'cursor-default'} ${s.alert ? 'border-[#C05C3D]/50' : s.warn ? 'border-amber-300/40' : 'border-white/10'}`}
                  data-testid={`integrity-${s.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                >
                  <p className="text-[10px] uppercase tracking-[0.14em] text-stone-300 font-bold">{s.label}</p>
                  <p className={`text-2xl font-semibold font-heading ${s.hi ? 'text-[#34D399]' : s.alert ? 'text-[#FCA5A5]' : s.warn ? 'text-amber-200' : 'text-white'}`}>{s.value}</p>
                </button>
              ))}
            </div>
            {integrity.approved_without_credential_30d > 0 && (
              <p className="text-xs text-amber-200 mt-3">⚠️ {integrity.approved_without_credential_30d} approved pro(s) haven&apos;t verified any credential in 30+ days.</p>
            )}
          </div>
        </div>
      )}

      {/* Metrics */}
      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Total Pros', value: metrics.total_professionals },
            { label: 'Pending', value: metrics.pending_professionals, hi: metrics.pending_professionals > 0 },
            { label: 'Approved', value: metrics.approved_professionals },
            { label: 'Bookings', value: metrics.total_bookings },
            { label: 'Revenue (mock)', value: metrics.revenue_by_currency.length > 0 ? `${metrics.revenue_by_currency[0].currency} ${metrics.revenue_by_currency[0].commission.toFixed(0)}` : '—' },
          ].map((m) => (
            <div key={m.label} className={`bg-white rounded-2xl p-4 border ${m.hi ? 'border-amber-300 bg-amber-50/40' : 'border-stone-100'}`}>
              <p className="text-[10px] uppercase tracking-[0.14em] text-stone-500 font-bold">{m.label}</p>
              <p className="text-2xl font-semibold text-[#1A4331] font-heading">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Commission control */}
      <div className="bg-white rounded-2xl p-5 border border-stone-100">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.14em] text-stone-500 font-bold flex items-center gap-2"><CurrencyDollar size={14} weight="duotone" /> Marketplace Commission</p>
            <p className="text-sm text-stone-600 mt-1">Percentage Nova retains from every booking. Default 10%.</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={commissionPct}
              onChange={(e) => setCommissionPct(e.target.value)}
              min={0} max={50} step="0.5"
              className="w-24 bg-white border border-stone-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#1A4331]"
              data-testid="admin-commission-input"
            />
            <span className="text-sm text-stone-500 font-semibold">%</span>
            <button onClick={handleSaveCommission} disabled={savingCommission} className="bg-[#1A4331] text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-[#2A5A44] disabled:opacity-50" data-testid="admin-commission-save">
              {savingCommission ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-stone-100 pb-2 -mt-2">
        {[
          { id: 'professionals', label: `Professionals (${pros.length})` },
          { id: 'credentials', label: `Credentials (${credentials.filter((c) => !c.verified).length} pending)` },
          { id: 'consultations', label: `Requests (${consultations.filter((c) => c.status === 'pending').length} pending)` },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`px-4 py-2 text-sm font-semibold rounded-full transition-all ${subTab === t.id ? 'bg-[#1A4331] text-white' : 'bg-white text-stone-600 hover:bg-stone-100 border border-stone-200'}`}
            data-testid={`admin-wellness-subtab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Filter (only for professionals subtab) */}
      {subTab === 'professionals' && (
      <div className="flex flex-wrap gap-2">
        {['pending', 'approved', 'rejected', ''].map((s) => (
          <button
            key={s || 'all'}
            onClick={() => setStatusFilter(s)}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-all ${statusFilter === s ? 'bg-[#1A4331] text-white border-[#1A4331]' : 'bg-white text-stone-600 border-stone-200 hover:border-[#1A4331]'}`}
            data-testid={`admin-filter-${s || 'all'}`}
          >
            {s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}
          </button>
        ))}
      </div>
      )}

      {/* Professionals list */}
      {subTab === 'professionals' && (pros.length === 0 ? (
        <div className="bg-white rounded-3xl border border-stone-100 p-12 text-center">
          <Sparkle size={28} weight="duotone" className="text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500">No professionals in this status.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {pros.map((p) => {
            const cat = getCategoryById(p.category_id);
            return (
              <div key={p.professional_id} className="bg-white rounded-2xl p-5 border border-stone-100 flex flex-col md:flex-row md:items-center gap-4" data-testid={`admin-pro-${p.professional_id}`}>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <p className="font-semibold text-[#1C1917]">{p.full_name}</p>
                    <span className={`text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${STATUS_STYLES[p.status] || STATUS_STYLES.pending}`}>{p.status}</span>
                    {p.nova_verified && <span className="text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 inline-flex items-center gap-1"><ShieldCheck size={10} weight="fill" /> Verified</span>}
                    {p.credential_verified && <span className="text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 inline-flex items-center gap-1"><CheckCircle size={10} weight="fill" /> Credential</span>}
                  </div>
                  <p className="text-sm text-stone-500">{p.professional_type} · {cat?.name}</p>
                  <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-stone-500">
                    <span className="inline-flex items-center gap-1"><MapPin size={12} weight="fill" /> {p.city}, {p.country}</span>
                    <span className="inline-flex items-center gap-1"><Clock size={12} weight="fill" /> {p.years_experience || 0} yrs</span>
                    {p.price_from > 0 && <span>From {formatPrice(p.price_from, p.currency)}</span>}
                    <span className="text-stone-400">· {p.email}</span>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => setSelected(p)} className="bg-white border border-stone-200 hover:border-[#1A4331] text-stone-700 p-2 rounded-full" title="View" data-testid={`admin-view-${p.professional_id}`}>
                    <Eye size={16} weight="duotone" />
                  </button>
                  {p.status === 'pending' && (
                    <>
                      <button onClick={() => handleApprove(p.professional_id)} className="bg-emerald-600 text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-emerald-700 inline-flex items-center gap-1" data-testid={`admin-approve-${p.professional_id}`}>
                        <Check size={14} weight="bold" /> Approve
                      </button>
                      <button onClick={() => handleReject(p.professional_id)} className="bg-white border border-red-200 text-red-700 px-4 py-2 rounded-full text-sm font-semibold hover:bg-red-50 inline-flex items-center gap-1" data-testid={`admin-reject-${p.professional_id}`}>
                        <X size={14} weight="bold" /> Reject
                      </button>
                    </>
                  )}
                  {p.status === 'approved' && !p.credential_verified && (
                    <button onClick={() => handleCredentialVerify(p.professional_id)} className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-blue-700 inline-flex items-center gap-1" data-testid={`admin-credverify-${p.professional_id}`}>
                      <CheckCircle size={14} weight="bold" /> Verify Credential
                    </button>
                  )}
                  <button onClick={() => handleDeletePro(p.professional_id, p.full_name)}
                    className="bg-white border border-red-200 text-red-600 p-2 rounded-full hover:bg-red-50" title="Delete professional"
                    data-testid={`admin-delete-pro-${p.professional_id}`}>
                    <Trash size={15} weight="duotone" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {/* Credentials sub-tab */}
      {subTab === 'credentials' && (
        credentials.length === 0 ? (
          <div className="bg-white rounded-3xl border border-stone-100 p-12 text-center">
            <CheckCircle size={28} weight="duotone" className="text-stone-300 mx-auto mb-3" />
            <p className="text-stone-500">No credentials submitted yet.</p>
          </div>
        ) : (
          <div className="grid gap-3" data-testid="admin-credentials-list">
            {credentials.map((c) => (
              <div key={c.credential_id} className="bg-white rounded-2xl p-4 border border-stone-100 flex items-center gap-4" data-testid={`admin-cred-${c.credential_id}`}>
                <div className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 ${c.verified ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                  <CheckCircle size={22} weight={c.verified ? 'fill' : 'duotone'} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-[#1C1917] truncate">{c.title}</p>
                    <span className={`text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full ${c.verified ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                      {c.verified ? 'Verified' : 'Pending'}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.14em] text-stone-400 font-bold">{c.kind}</span>
                  </div>
                  <p className="text-xs text-stone-500 truncate">
                    {c.professional_name} · {c.issuer || 'No issuer'} {c.issue_year ? `· ${c.issue_year}` : ''}
                  </p>
                  <a href={c.document_url} target="_blank" rel="noreferrer" className="text-xs text-[#1A4331] hover:underline truncate">View document →</a>
                </div>
                {c.verified ? (
                  <button onClick={() => handleUnverifyCred(c.credential_id)} className="bg-white border border-stone-200 text-stone-700 px-4 py-2 rounded-full text-sm font-semibold hover:border-red-300 hover:text-red-700" data-testid={`admin-cred-unverify-${c.credential_id}`}>Unverify</button>
                ) : (
                  <button onClick={() => handleVerifySingleCred(c.credential_id)} className="bg-emerald-600 text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-emerald-700 inline-flex items-center gap-1" data-testid={`admin-cred-verify-${c.credential_id}`}>
                    <Check size={14} weight="bold" /> Verify
                  </button>
                )}
              </div>
            ))}
          </div>
        )
      )}

      {/* Consultation requests sub-tab */}
      {subTab === 'consultations' && (
        consultations.length === 0 ? (
          <div className="bg-white rounded-3xl border border-stone-100 p-12 text-center">
            <Sparkle size={28} weight="duotone" className="text-stone-300 mx-auto mb-3" />
            <p className="text-stone-500">No consultation requests yet.</p>
          </div>
        ) : (
          <div className="grid gap-3" data-testid="admin-consultations-list">
            {consultations.map((c) => (
              <div key={c.request_id} className="bg-white rounded-2xl p-4 border border-stone-100" data-testid={`admin-consultation-${c.request_id}`}>
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <p className="font-semibold text-[#1C1917]">{c.customer_name} → {c.professional_name}</p>
                  <span className={`text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full ${c.status === 'accepted' ? 'bg-emerald-50 text-emerald-700' : c.status === 'declined' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>{c.status}</span>
                </div>
                <p className="text-xs text-stone-500">{c.customer_email} · {c.goal}</p>
                {c.resulting_booking_id && <p className="text-xs text-emerald-700 mt-0.5">→ Booking: {c.resulting_booking_id}</p>}
              </div>
            ))}
          </div>
        )
      )}

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-3xl max-w-2xl w-full p-6 md:p-8 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-pro-detail">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.14em] text-stone-500 font-bold">{selected.professional_type}</p>
                <h3 className="text-2xl font-semibold text-[#1C1917] font-heading tracking-tight">{selected.full_name}</h3>
              </div>
              <button onClick={() => setSelected(null)} className="w-9 h-9 rounded-full bg-stone-100 flex items-center justify-center"><X size={18} weight="bold" /></button>
            </div>
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <div><dt className="text-stone-500">Email</dt><dd className="font-semibold">{selected.email}</dd></div>
              <div><dt className="text-stone-500">Phone</dt><dd className="font-semibold">{selected.phone}</dd></div>
              <div><dt className="text-stone-500">Location</dt><dd className="font-semibold">{selected.city}, {selected.country}</dd></div>
              <div><dt className="text-stone-500">Experience</dt><dd className="font-semibold">{selected.years_experience || 0} years</dd></div>
              <div><dt className="text-stone-500">Modes</dt><dd className="font-semibold">{(selected.modes || []).join(', ')}</dd></div>
              <div><dt className="text-stone-500">Languages</dt><dd className="font-semibold">{(selected.languages || []).join(', ')}</dd></div>
              <div><dt className="text-stone-500">Starting Price</dt><dd className="font-semibold">{selected.price_from > 0 ? formatPrice(selected.price_from, selected.currency) : 'On request'}</dd></div>
              <div><dt className="text-stone-500">Status</dt><dd className="font-semibold capitalize">{selected.status}</dd></div>
            </dl>
            {selected.headline && <div className="mt-4"><p className="text-xs uppercase tracking-[0.14em] text-stone-500 font-bold">Headline</p><p className="text-sm mt-1">{selected.headline}</p></div>}
            {selected.bio && <div className="mt-4"><p className="text-xs uppercase tracking-[0.14em] text-stone-500 font-bold">Bio</p><p className="text-sm mt-1 whitespace-pre-line">{selected.bio}</p></div>}
            {selected.rejection_reason && <div className="mt-4 bg-red-50 border border-red-200 rounded-2xl p-3 text-sm text-red-700"><strong>Rejection reason:</strong> {selected.rejection_reason}</div>}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWellnessTab;
